import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ParkingSpaceService } from '../../../../api/gen/api/parkingSpace.service';
import { OperatorGroupService } from '../../../../api/gen/api/operatorGroup.service';
import { ParkingSpaceDto } from '../../../../api/gen/model/parkingSpaceDto';
import { OperatorGroupDto } from '../../../../api/gen/model/operatorGroupDto';
import { SortOrder } from '../../../../api/gen/model/sortOrder';

export interface AssignParkingSpaceGroupDialogData {
  group: OperatorGroupDto;
}

@Component({
  selector: 'app-assign-parkingspace-group',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ],
  templateUrl: './assign-parkingspace-group.component.html',
  styleUrl: './assign-parkingspace-group.component.scss'
})
export class AssignParkingspaceGroupComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<AssignParkingspaceGroupComponent>);
  private data = inject<AssignParkingSpaceGroupDialogData>(MAT_DIALOG_DATA);
  private parkingSpaceService = inject(ParkingSpaceService);
  private operatorGroupService = inject(OperatorGroupService);
  private snackBar = inject(MatSnackBar);

  availableSpaces = signal<ParkingSpaceDto[]>([]);
  selectedSpaceId = signal('');
  isLoading = signal(false);
  isAssigning = signal(false);

  get group(): OperatorGroupDto {
    return this.data.group;
  }

  get alreadyAssignedIds(): Set<string> {
    return new Set(
      (this.group.parkingSpaceOperators ?? []).map(p => p.parkingSpaceId!).filter(Boolean)
    );
  }

  ngOnInit(): void {
    this.loadAvailableSpaces();
  }

  private loadAvailableSpaces(): void {
    this.isLoading.set(true);
    this.parkingSpaceService.parkingSpaceFilterPost(
      undefined, undefined, undefined, undefined, undefined, undefined, 0, 100, 'name', SortOrder.Ascending
    ).subscribe({
      next: (result) => {
        const assigned = this.alreadyAssignedIds;
        this.availableSpaces.set(
          (result?.items ?? []).filter(s => s.id && !assigned.has(s.id))
        );
        this.isLoading.set(false);
      },
      error: () => {
        this.snackBar.open('Failed to load parking spaces', 'Close', { duration: 3000 });
        this.isLoading.set(false);
      }
    });
  }

  onAssign(): void {
    const spaceId = this.selectedSpaceId();
    const groupId = this.group.id;
    if (!spaceId || !groupId) return;

    this.isAssigning.set(true);
    this.operatorGroupService.operatorGroupsOperatorGroupIdParkingSpacesPost(
      groupId,
      { parkingSpaceId: spaceId }
    ).subscribe({
      next: () => {
        this.snackBar.open('Parking space assigned to group successfully', 'Close', { duration: 3000 });
        this.dialogRef.close(true);
      },
      error: () => {
        this.snackBar.open('Failed to assign parking space to group', 'Close', { duration: 5000 });
        this.isAssigning.set(false);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
